'use client';

import { useAtom } from 'jotai';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { usePostHog } from '~/hooks/usePostHog';
import { twMerge } from 'tailwind-merge';
import TermsAndConditionsAgreement from '~/app/[locale]/auth/_components/TermsAndConditionsAgreement';
import { registrationFormAtom, RegistrationFormData } from '~/atoms';
import { Icon, IconName } from '~/components/core/Icon';
import AddressFieldAutoComplete, {
  Address,
} from '~/components/customComponents/address/AddressFieldAutoComplete';
import Button from '~/components/customComponents/buttons/Button';
import Flex from '~/components/customComponents/flex/Flex';
import { NumberInput } from '~/components/customComponents/inputs/NumberInput';
import PhoneInput from '~/components/customComponents/inputs/PhoneInput';
import { TextInput } from '~/components/customComponents/inputs/TextInput';
import Modal from '~/components/customComponents/modal/Modal';
import SimpleSelect from '~/components/customComponents/select/SimpleSelect';
import Text from '~/components/customComponents/texts/Text';
import { AndercoreExternalLinks } from '~/constants/endpoints';
import { ROUTES_MAP } from '~/constants/routes';
import { useCurrentLocale, useI18n } from '~/locales/client';
import { FormItemTypeEnum } from '~/types-and-enums/formFieldEnums';
import { ButtonTypesEnum } from '~/types-and-enums/generalTypes';
import { ColorModeEnum } from '~/utils/generalUtils';
import { colors, spacing } from '~/utils/tailwindUtils';

/**
 * Registration Modal Component
 *
 * Displays a 3-step registration form for supplier registration.
 */
const RegistrationModal = () => {
  const router = useRouter();
  const translate = useI18n();
  const locale = useCurrentLocale();
  const [formData, setFormData] = useAtom(registrationFormAtom);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof RegistrationFormData, string>>>({});
  const [address, setAddress] = useState<Address | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Business types with translated labels
  const BUSINESS_TYPES = useMemo(
    () => [
      { id: 1, value: 'Distributor', label: translate('general.business-type.distributor') },
      {
        id: 2,
        value: 'Installer',
        label: translate('general.business-type.contractor'),
      },
      {
        id: 3,
        value: 'Project Developer',
        label: translate('general.business-type.project-developer'),
      },
      { id: 4, value: 'Trader', label: translate('general.business-type.trader') },
      { id: 5, value: 'EPC', label: 'EPC' },
      {
        id: 6,
        value: 'Consultancy',
        label: translate('general.business-type.consultancy'),
      },
      { id: 7, value: 'Investor', label: translate('general.business-type.investor') },
      {
        id: 8,
        value: 'Planning company',
        label: translate('general.business-type.planning-company'),
      },
      { id: 9, value: 'Other', label: translate('general.business-type.other') },
    ],
    [translate],
  );

  // Initialize address from persisted form data on mount
  useEffect(() => {
    if (
      !address &&
      (formData.street || formData.city || formData.postal_code || formData.country)
    ) {
      setAddress({
        street: formData.street,
        city: formData.city,
        postal_code: formData.postal_code,
        country: formData.country || null,
        state: null,
        state_code: null,
        country_code: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form data when address changes
  useEffect(() => {
    if (address) {
      setFormData((prev) => ({
        ...prev,
        street: address.street,
        city: address.city,
        postal_code: address.postal_code,
        country: address.country || '',
      }));
    }
  }, [address, setFormData]);

  const handleClose = (): void => {
    // Navigate to register-success page which will display the success modal
    window.location.href = AndercoreExternalLinks[locale].andercoreHome;
  };

  const validateStep1 = (): boolean => {
    const newErrors: Partial<Record<keyof RegistrationFormData, string>> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = translate('general.first-name-required');
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = translate('general.last-name-required');
    }
    if (!formData.email_address.trim()) {
      newErrors.email_address = translate('page.general.email-is-required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) {
      newErrors.email_address = translate('general.invalid-email');
    }
    if (!formData.telephone_number.trim()) {
      newErrors.telephone_number = translate('general.telephone-number-required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Partial<Record<keyof RegistrationFormData, string>> = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = translate('general.company-name-required');
    }
    if (!formData.vat_id.trim()) {
      newErrors.vat_id = translate('general.vat-id-required');
    }
    if (!formData.business_type) {
      newErrors.business_type = translate('general.business-type-required');
    }
    if (!formData.number_of_employees.trim()) {
      newErrors.number_of_employees = translate('general.number-of-employees-required');
    }
    // Validate address fields individually
    if (!formData.street?.trim()) {
      newErrors.street = translate('general.street-is-missing');
    }
    if (!formData.city?.trim()) {
      newErrors.city = translate('general.city-is-missing');
    }
    if (!formData.postal_code?.trim()) {
      newErrors.postal_code = translate('general.postal-code-is-missing');
    }
    // Country is always provided by Google Places API, so no need to validate
    if (!formData.annual_revenue_in_net_eur.trim()) {
      newErrors.annual_revenue_in_net_eur = translate('general.annual-revenue-required');
    }
    if (!formData.annual_purchase_volume_eur.trim()) {
      newErrors.annual_purchase_volume_eur = translate('general.annual-purchase-volume-required');
    }
    if (!termsAccepted) {
      setError(translate('general.terms-required'));
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
        setError(null);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setError(null);
    }
  };

  const { captureEvent } = usePostHog();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateStep2()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email_address,
        company_name: formData.company_name,
        phone: formData.telephone_number,
        vat_id: formData.vat_id,
        number_of_employees: formData.number_of_employees
          ? parseInt(formData.number_of_employees, 10)
          : 0,
        type_of_business: formData.business_type,
        street: formData.street,
        city: formData.city,
        country: formData.country,
        postal_code: formData.postal_code,
        annual_revenue_eur: formData.annual_revenue_in_net_eur
          ? parseFloat(formData.annual_revenue_in_net_eur)
          : 0,
        annual_purchase_volume_eur: formData.annual_purchase_volume_eur
          ? parseFloat(formData.annual_purchase_volume_eur)
          : 0,
      };

      await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // The requirement is to move to register success page, no matter the response from the API is ok or not
    } catch (err: unknown) {
      console.error('Registration error:', err);
    } finally {
      captureEvent('user_registration_completed', { email: formData.email_address });
      // The requirement is to move to register success page, no matter the response from the API is ok or not
      router.replace(`/${locale}${ROUTES_MAP.authRegisterSuccess}`, { scroll: false });
    }
  };

  const updateField = (field: keyof RegistrationFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Modal
      isOpen={true}
      closeModal={handleClose}
      classname="pt-0 pb-6 md:pt-0 lg:pt-0 lg:pb-10"
      size="lg"
    >
      <Flex className="flex-col gap-6 w-full max-w-xl mx-auto">
        {/* Progress Indicator */}
        <Flex className="items-center justify-center gap-1 md:gap-2">
          {/* Step 1 */}
          <Flex className="items-center gap-2">
            <Flex
              className={twMerge(
                'w-6 h-6 md:w-8 md:h-8 rounded-full items-center justify-center',
                currentStep >= 1 ? 'bg-gray-800' : 'bg-gray-300',
              )}
            >
              {currentStep > 1 ? (
                <Icon name={IconName.CheckCircle} size={spacing[4]} color={colors.white} />
              ) : (
                <Text
                  className={twMerge(
                    'font-semibold',
                    currentStep >= 1 ? 'text-white' : 'text-gray-600',
                  )}
                >
                  1
                </Text>
              )}
            </Flex>
            <Text className="font-medium text-gray-700">{translate('general.contact')}</Text>
          </Flex>
          <div
            className={twMerge('w-12 h-0.5', currentStep >= 2 ? 'bg-gray-800' : 'bg-gray-300')}
          />
          {/* Step 2 */}
          <Flex className="items-center gap-2">
            <Flex
              className={twMerge(
                'w-6 h-6 md:w-8 md:h-8 rounded-full items-center justify-center',
                currentStep >= 2 ? 'bg-gray-800' : 'bg-gray-300',
              )}
            >
              {currentStep > 2 ? (
                <Icon name={IconName.CheckCircle} size={spacing[4]} color={colors.white} />
              ) : (
                <Text
                  className={twMerge(
                    'font-semibold',
                    currentStep >= 2 ? 'text-white' : 'text-gray-600',
                  )}
                >
                  2
                </Text>
              )}
            </Flex>
            <Text className="font-medium text-gray-700">{translate('general.company')}</Text>
          </Flex>
        </Flex>
        {/* Title - Only show for steps 1 and 2 */}
        <Text className="text-xl md:text-2xl font-bold text-gray-900">
          {currentStep === 1
            ? translate('general.create-user-account')
            : translate('general.configure-details')}
        </Text>
        {/* Error Message */}
        {error && (
          <Flex className="bg-red-50 border border-red-200 rounded-lg p-3">
            <Text className="text-sm text-red-700">{error}</Text>
          </Flex>
        )}
        {/* Form */}
        <form
          onSubmit={currentStep === 2 ? handleSubmit : (e) => e.preventDefault()}
          className="flex flex-col gap-6"
        >
          {currentStep === 1 ? (
            <>
              {/* Step 1 Fields */}
              <Flex className="flex-col md:flex-row gap-4">
                <Flex className="flex-col gap-2 flex-1">
                  <label htmlFor="first_name" className="text-sm font-medium text-gray-700">
                    {translate('general.first-name')} *
                  </label>
                  <TextInput
                    name="first_name"
                    type={FormItemTypeEnum.Text}
                    value={formData.first_name}
                    onChange={(e) => updateField('first_name', e.target.value)}
                    placeholder="John"
                    classNames={twMerge(
                      'bg-gray-50 border-gray-300',
                      errors.first_name && 'border-red-500',
                    )}
                    autoComplete="given-name"
                  />
                  {errors.first_name && (
                    <Text className="text-xs text-red-600">{errors.first_name}</Text>
                  )}
                </Flex>

                <Flex className="flex-col gap-2 flex-1">
                  <label htmlFor="last_name" className="text-sm font-medium text-gray-700">
                    {translate('general.last-name')} *
                  </label>
                  <TextInput
                    name="last_name"
                    type={FormItemTypeEnum.Text}
                    value={formData.last_name}
                    onChange={(e) => updateField('last_name', e.target.value)}
                    placeholder="Doe"
                    classNames={twMerge(
                      'bg-gray-50 border-gray-300',
                      errors.last_name && 'border-red-500',
                    )}
                    autoComplete="family-name"
                  />
                  {errors.last_name && (
                    <Text className="text-xs text-red-600">{errors.last_name}</Text>
                  )}
                </Flex>
              </Flex>

              <Flex className="flex-col gap-2">
                <label htmlFor="email_address" className="text-sm font-medium text-gray-700">
                  {translate('general.email')} *
                </label>
                <TextInput
                  name="email_address"
                  type={FormItemTypeEnum.Text}
                  value={formData.email_address}
                  onChange={(e) => updateField('email_address', e.target.value)}
                  placeholder="jane.doe@company.com"
                  classNames={twMerge(
                    'bg-gray-50 border-gray-300',
                    errors.email_address && 'border-red-500',
                  )}
                  autoComplete="email"
                />
                {errors.email_address && (
                  <Text className="text-xs text-red-600">{errors.email_address}</Text>
                )}
              </Flex>

              <Flex className="flex-col gap-2">
                <label htmlFor="telephone_number" className="text-sm font-medium text-gray-700">
                  {translate('general.telephone-number')} *
                </label>
                <PhoneInput
                  name="telephone_number"
                  value={formData.telephone_number}
                  onChange={(value) => updateField('telephone_number', value)}
                  placeholder="176 0000 0000"
                  classNames={twMerge(errors.telephone_number && 'border-red-500')}
                />
                {errors.telephone_number && (
                  <Text className="text-xs text-red-600">{errors.telephone_number}</Text>
                )}
              </Flex>

              {/* Continue Button */}
              <Button
                type="button"
                buttonStyle={ButtonTypesEnum.primary}
                text={translate('general.continue')}
                onClick={(e) => {
                  e.preventDefault();
                  handleNext();
                }}
                buttonClassName="bg-red-500 hover:bg-red-600 rounded-lg py-2.5"
              />
            </>
          ) : (
            <>
              {/* Step 2 Fields */}
              <Flex className="flex-col gap-6 overflow-y-auto max-h-[50vh] md:max-h-lvh">
                {/* Company name and VAT ID */}
                <Flex className="flex-col md:flex-row gap-4 w-full">
                  <Flex className="flex-col gap-2 flex-1 min-w-0">
                    <label htmlFor="company_name" className="text-sm font-medium text-gray-700">
                      {translate('general.company-name')} *
                    </label>
                    <TextInput
                      name="company_name"
                      type={FormItemTypeEnum.Text}
                      value={formData.company_name}
                      onChange={(e) => updateField('company_name', e.target.value)}
                      placeholder={translate('general.enter-legal-entity-name')}
                      classNames={twMerge(
                        'bg-gray-50 border-gray-300',
                        errors.company_name && 'border-red-500',
                      )}
                      autoComplete="organization"
                    />
                    {errors.company_name && (
                      <Text className="text-xs text-red-600">{errors.company_name}</Text>
                    )}
                  </Flex>

                  <Flex className="flex-col gap-2 flex-1 min-w-0">
                    <label htmlFor="vat_id" className="text-sm font-medium text-gray-700">
                      {translate('general.vat-id')} *
                    </label>
                    <TextInput
                      name="vat_id"
                      type={FormItemTypeEnum.Text}
                      value={formData.vat_id}
                      onChange={(e) => updateField('vat_id', e.target.value)}
                      placeholder="e.g. DE123456789"
                      classNames={twMerge(
                        'bg-gray-50 border-gray-300',
                        errors.vat_id && 'border-red-500',
                      )}
                      autoComplete="off"
                    />
                    {errors.vat_id && <Text className="text-xs text-red-600">{errors.vat_id}</Text>}
                  </Flex>
                </Flex>

                {/* Type of Business and Number of employees */}
                <Flex className="flex-col md:flex-row gap-4 w-full">
                  <Flex className="flex-col gap-2 flex-1 min-w-0">
                    <label htmlFor="business_type" className="text-sm font-medium text-gray-700">
                      {translate('general.type-of-business')} *
                    </label>
                    <SimpleSelect
                      name="business_type"
                      options={BUSINESS_TYPES}
                      value={formData.business_type}
                      onChange={(value) => updateField('business_type', value || '')}
                      placeholder={translate('general.select')}
                      emptyLabel={translate('general.no-options')}
                      selectContainerClassName={twMerge(errors.business_type && 'border-red-500')}
                    />
                    {errors.business_type && (
                      <Text className="text-xs text-red-600">{errors.business_type}</Text>
                    )}
                  </Flex>

                  <Flex className="flex-col gap-2 flex-1 min-w-0">
                    <label
                      htmlFor="number_of_employees"
                      className="text-sm font-medium text-gray-700"
                    >
                      {translate('general.number-of-employees')} *
                    </label>
                    <NumberInput
                      name="number_of_employees"
                      value={formData.number_of_employees}
                      onChange={(e) => updateField('number_of_employees', e.target.value)}
                      placeholder="e.g. 50"
                      classNames={twMerge(
                        'bg-gray-50 border-gray-300 w-full',
                        errors.number_of_employees && 'border-red-500',
                      )}
                    />
                    {errors.number_of_employees && (
                      <Text className="text-xs text-red-600">{errors.number_of_employees}</Text>
                    )}
                  </Flex>
                </Flex>

                {/* Annual Revenue and Annual Purchase Volume */}
                <Flex className="flex-col md:flex-row gap-4 w-full">
                  <Flex className="flex-col gap-2 flex-1 min-w-0">
                    <label
                      htmlFor="annual_revenue_in_net_eur"
                      className="text-sm font-medium text-gray-700"
                    >
                      {translate('general.annual-revenue')} *
                    </label>
                    <NumberInput
                      name="annual_revenue_in_net_eur"
                      value={formData.annual_revenue_in_net_eur}
                      onChange={(e) => updateField('annual_revenue_in_net_eur', e.target.value)}
                      placeholder="e.g. 5.000.000"
                      classNames={twMerge(
                        'bg-gray-50 border-gray-300 w-full',
                        errors.annual_revenue_in_net_eur && 'border-red-500',
                      )}
                    />
                    {errors.annual_revenue_in_net_eur && (
                      <Text className="text-xs text-red-600">
                        {errors.annual_revenue_in_net_eur}
                      </Text>
                    )}
                  </Flex>

                  <Flex className="flex-col gap-2 flex-1 min-w-0">
                    <label
                      htmlFor="annual_purchase_volume_eur"
                      className="text-sm font-medium text-gray-700"
                    >
                      {translate('general.annual-purchase-volume')} *
                    </label>
                    <NumberInput
                      name="annual_purchase_volume_eur"
                      value={formData.annual_purchase_volume_eur}
                      onChange={(e) => updateField('annual_purchase_volume_eur', e.target.value)}
                      placeholder="e.g. 2.000.000"
                      classNames={twMerge(
                        'bg-gray-50 border-gray-300 w-full',
                        errors.annual_purchase_volume_eur && 'border-red-500',
                      )}
                    />
                    {errors.annual_purchase_volume_eur && (
                      <Text className="text-xs text-red-600">
                        {errors.annual_purchase_volume_eur}
                      </Text>
                    )}
                  </Flex>
                </Flex>

                <Flex className="flex-col gap-2">
                  <label htmlFor="address" className="text-sm font-medium text-gray-700">
                    {translate('general.full-address')} *
                  </label>
                  <AddressFieldAutoComplete
                    value={address || undefined}
                    onChange={(addr) => {
                      setAddress(addr);
                      updateField('street', addr.street);
                      updateField('city', addr.city);
                      updateField('postal_code', addr.postal_code);
                      updateField('country', addr.country || '');
                    }}
                    placeholder="e.g. Musterstraße 123, 12345 Musterstadt"
                    error={!!(errors.street || errors.city || errors.postal_code)}
                  />
                  {(errors.street || errors.city || errors.postal_code) && (
                    <Text className="text-xs text-red-600">
                      {[errors.street, errors.city, errors.postal_code].filter(Boolean).join(', ')}
                    </Text>
                  )}
                </Flex>
              </Flex>

              {/* Terms and Conditions */}
              <Flex className="flex-col gap-2">
                <Flex
                  className="items-start gap-2"
                  onClick={() => {
                    setTermsAccepted(!termsAccepted);
                    setError(null);
                  }}
                >
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={termsAccepted}
                    aria-labelledby="terms-label"
                    className="mt-1 rounded transition-all"
                  >
                    <Icon
                      name={termsAccepted ? IconName.CheckboxChecked : IconName.CheckboxUnchecked}
                      size={spacing[4]}
                      color={termsAccepted ? colors.red[500] : colors.gray[500]}
                      colorMode={termsAccepted ? ColorModeEnum.Fill : ColorModeEnum.Stroke}
                      className="cursor-pointer"
                    />
                  </button>
                  <label
                    id="terms-label"
                    htmlFor="terms"
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    <TermsAndConditionsAgreement />
                  </label>
                </Flex>
              </Flex>

              {/* Action Buttons */}
              <Flex className="gap-4 justify-between">
                <Button
                  type="button"
                  buttonStyle={ButtonTypesEnum.default}
                  text={translate('general.back')}
                  onClick={(e) => {
                    e.preventDefault();
                    handleBack();
                  }}
                  buttonClassName="rounded-lg border border-gray-200 hover:border-gray-300 py-2.5 px-6 max-w-fit"
                />
                <Button
                  type="submit"
                  buttonStyle={ButtonTypesEnum.primary}
                  text={translate('general.complete-registration')}
                  isLoading={loading}
                  disabled={loading || !termsAccepted}
                  buttonClassName="bg-red-500 hover:bg-red-600 rounded-lg py-2.5 max-w-[50%]"
                />
              </Flex>
            </>
          )}
        </form>
      </Flex>
    </Modal>
  );
};

export default RegistrationModal;
